import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import usePlatformStore from "@store/usePlatformStore";
import emailService from "@services/email.service";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { REMOVE_EMAIL_MUTATION_KEY, THREAD_QUERY_KEY } from "@config/query";
import { UnsubscribeThreadQueryData } from "@app-types/requests.type";

export function useUnsubscribeThread() {
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { isComposeMode } = usePlatformStore();

  return useMutation({
    mutationKey: [REMOVE_EMAIL_MUTATION_KEY],
    mutationFn: async (data: Partial<UnsubscribeThreadQueryData>) => {
      return await emailService.unsubscribeThread(data.threadId, data.body);
    },
    onSuccess: (response, data) => {
      void queryClient.invalidateQueries({ queryKey: [THREAD_QUERY_KEY, data.threadId] });

      if (!data.body?.remove_all_emails_except) {
        ToastService.showSuccess("Successfully Unsubscribed");
      } else {
        ToastService.showSuccess("All Other Emails Successfully Removed");
      }

      if (isComposeMode) {
        // Update the persistent toast message from "Thread will sync..." to "Email will sync..."
        ToastService.showSuccess("Email will sync when sent", {
          id: "sync-when-sent",
          duration: Infinity,
        });
      }
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.UNSUBSCRIBE);
      ToastService.showError(error as ApiError, ERROR_MESSAGES.UNSUBSCRIBE);
    },
  });
}
